import { useEffect, useState } from "react";
import {
  apiErrorMessage,
  type Me,
  type RaceApi,
  type RideRecord,
} from "../api";
import { formatDistance, formatElapsed, formatPower } from "../lib/format";
import { formatGapMs, formatRaceTime } from "../race/format";
import { AppHeader } from "./AppHeader";

interface HistoryScreenProps {
  api: RaceApi;
  me: Me | null;
  onSignIn(): void;
  onHome(): void;
  onProfile(): void;
  onSignOut(): void;
  onOpenResults(raceId: string): void;
  onRideAgain(): void;
}

function formatDate(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  return new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function HistoryScreen({
  api,
  me,
  onSignIn,
  onHome,
  onProfile,
  onSignOut,
  onOpenResults,
  onRideAgain,
}: HistoryScreenProps) {
  const [rides, setRides] = useState<RideRecord[]>([]);
  const [loading, setLoading] = useState(Boolean(me));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!me) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    void api
      .getRides(controller.signal)
      .then((next) => {
        if (controller.signal.aborted) return;
        setRides(next);
        setError(null);
      })
      .catch((cause) => {
        if (controller.signal.aborted) return;
        setError(apiErrorMessage(cause));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [api, me]);

  return (
    <main className="page-screen history-screen" data-testid="history">
      <AppHeader
        me={me}
        onHome={onHome}
        onSignIn={onSignIn}
        onSignOut={onSignOut}
        onProfile={onProfile}
        onHistory={() => undefined}
        compact
      />

      <div className="history-page">
        <div className="history-page__head">
          <div>
            <p className="eyebrow">
              <span className="eyebrow__line" />
              Rider history
            </p>
            <h1 className="auth-title">Your rides.</h1>
            <p className="auth-sub">
              Free rides and persisted race results. Guest rides are never stored.
            </p>
          </div>
          <button type="button" className="ride-button ride-button--compact" onClick={onRideAgain}>
            Free ride
          </button>
        </div>

        {!me ? (
          <div className="history-empty">
            <p>Sign in to see your ride and race history.</p>
            <button type="button" className="ride-button ride-button--compact" onClick={onSignIn}>
              Sign in
            </button>
          </div>
        ) : loading ? (
          <p className="history-empty" role="status">
            Reading your rides…
          </p>
        ) : error ? (
          <div className="load-error" role="alert">
            <p>{error}</p>
          </div>
        ) : rides.length === 0 ? (
          <div className="history-empty">
            <p>No rides yet. A race result appears here after the finish.</p>
            <button type="button" className="text-button" onClick={onRideAgain}>
              Start a free ride
            </button>
          </div>
        ) : (
          <div className="history-list">
            {rides.map((ride) => {
              const isRace = Boolean(ride.eventId);
              return (
                <article className="history-row" key={ride.id}>
                  <div className="history-row__when">
                    <span className={`history-badge ${isRace ? "history-badge--race" : ""}`}>
                      {isRace ? "Race" : "Ride"}
                    </span>
                    <span>{formatDate(ride.startedAt)}</span>
                  </div>
                  <div className="history-row__name">
                    <strong>{ride.raceName ?? (isRace ? "Race result" : "Free ride")}</strong>
                    <span>
                      {isRace
                        ? ride.raceCategory
                          ? `Category ${ride.raceCategory}`
                          : "Race"
                        : (ride.worldVersion ? `World ${ride.worldVersion}` : "Cimara")}
                    </span>
                  </div>
                  <div className="history-row__stats">
                    <span>
                      <strong>{formatDistance(ride.distanceM)}</strong> km
                    </span>
                    <span>
                      <strong>
                        {formatElapsed(
                          isRace && ride.raceTimeMs !== null
                            ? ride.raceTimeMs
                            : ride.durationMs,
                        )}
                      </strong>
                    </span>
                    <span>
                      <strong>
                        {ride.averagePower === null ? "—" : formatPower(ride.averagePower)}
                      </strong>{" "}
                      W
                    </span>
                  </div>
                  <div className="history-row__result">
                    {isRace ? (
                      ride.raceStatus === "dnf" ? (
                        <span className="history-dnf">DNF</span>
                      ) : ride.racePosition !== null ? (
                        <span className="history-pos">{ride.racePosition}</span>
                      ) : (
                        <span>—</span>
                      )
                    ) : (
                      <span className="history-free">{formatRaceTime(ride.durationMs)}</span>
                    )}
                    {isRace && ride.raceGapMs !== null && ride.raceGapMs > 0 ? (
                      <span className="history-gap">{formatGapMs(ride.raceGapMs)}</span>
                    ) : null}
                    {isRace && ride.eventId ? (
                      <button
                        type="button"
                        className="text-button"
                        onClick={() => onOpenResults(ride.eventId ?? "")}
                      >
                        Results
                      </button>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}
